import { NonNegativeDecimalStringSchema, SignedDecimalStringSchema } from "./decimal-string";
import { Decimal } from "decimal.js";
import { z } from "zod";

/** Why a position (or the part of it) closed. */
export const BattleTradeCloseCauseSchema = z.enum([
  "USER",
  "TAKE_PROFIT",
  "STOP_LOSS",
  "LIQUIDATION",
  "ELIMINATION",
  "SETTLEMENT",
]);
export type BattleTradeCloseCause = z.infer<typeof BattleTradeCloseCauseSchema>;

/** One position from open to flat (or to the bell), replayed from the player's fills. A stored one has closed. */
export const BattleTradeSchema = z.object({
  id: z.string(),
  pair: z.string(),
  side: z.enum(["long", "short"]),
  leverage: z.number().positive(),
  /** Largest notional held, USD. */
  notionalUsd: NonNegativeDecimalStringSchema,
  entryPriceUsd: NonNegativeDecimalStringSchema,
  /** Average exit. */
  exitPriceUsd: NonNegativeDecimalStringSchema,
  openedAt: z.coerce.date(),
  /** Each later fill that grew the position. */
  addedAt: z.array(z.coerce.date()),
  closedAt: z.coerce.date(),
  realizedPnlUsd: SignedDecimalStringSchema,
  cause: BattleTradeCloseCauseSchema,
  /** Closed in several fills. */
  isPartial: z.boolean(),
});
export type BattleTradeDto = z.infer<typeof BattleTradeSchema>;

/**
 * Total PnL (realized + unrealized), integer cents, one point every `stepMs` from `startMs`. A knocked-out player's
 * series ends at the first point at or after their `out.at` (`seriesThroughKnockout`). The last point is the
 * player's settled `finalPnlUsd` (`seriesEndingOnSettledPnl`).
 */
export const BattlePnlSeriesSchema = z.object({
  startMs: z.number().int(),
  stepMs: z.number().int().positive(),
  pnlCents: z.array(z.number().int()),
});
export type BattlePnlSeriesDto = z.infer<typeof BattlePnlSeriesSchema>;

export const BattlePlayerOutSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("cut"), index: z.number().int().positive(), at: z.coerce.date() }),
  z.object({ kind: z.literal("drawdown"), at: z.coerce.date() }),
]);

export const BattleRecordPlayerSchema = z.object({
  hubUserId: z.number().int(),
  nickname: z.string().nullable(),
  /** Final place; null for a player the settlement did not rank. */
  rank: z.number().int().positive().nullable(),
  /** Realized PnL after the bell; a Battle Royale player cut out keeps the PnL frozen at the cut. */
  finalPnlUsd: SignedDecimalStringSchema,
  out: BattlePlayerOutSchema.nullable(),
  trades: z.array(BattleTradeSchema),
  series: BattlePnlSeriesSchema,
});
export type BattleRecordPlayerDto = z.infer<typeof BattleRecordPlayerSchema>;

export const BattleRecordCutSchema = z.object({
  index: z.number().int().positive(),
  at: z.coerce.date(),
  skipped: z.boolean(),
  /** Worst first; `pnlUsd` in cents, null for a player who never traded. */
  out: z.array(z.object({ hubUserId: z.number().int(), pnlUsd: SignedDecimalStringSchema.nullable() })),
});

/**
 * Bump on any change to the stored payload's shape or derivation, with the step that upgrades the previous version
 * (`battle-record-upgrade.ts`): a stored record is upgraded on read, never rebuilt. `battle-record.dto.spec.ts` pins
 * the schema's shape to this number and fails on a version with no upgrade step.
 */
export const ARENA_BATTLE_RECORD_SCHEMA_VERSION = 5;

/** The points of `series` up to the first at or after `outAtMs`, so a read at the knockout itself still interpolates. */
export function seriesThroughKnockout<Series extends BattlePnlSeriesDto>(series: Series, outAtMs: number): Series {
  const lastIndex = Math.max(0, Math.ceil((outAtMs - series.startMs) / series.stepMs));
  return lastIndex + 1 >= series.pnlCents.length
    ? series
    : { ...series, pnlCents: series.pnlCents.slice(0, lastIndex + 1) };
}

/** The series is marked at the bell or the knockout; the settled figure comes from a later close. */
export function seriesEndingOnSettledPnl<Series extends BattlePnlSeriesDto>(
  series: Series,
  finalPnlUsd: string,
): Series {
  return { ...series, pnlCents: [...series.pnlCents.slice(0, -1), new Decimal(finalPnlUsd).mul(100).toNumber()] };
}

/** A finished 3+ player Arena battle, built once at finalization. Every figure is final. */
export const ArenaBattleRecordSchema = z.object({
  campaignName: z.string(),
  epochId: z.number().int(),
  format: z.enum(["battle-royale", "spin-and-go", "classic"]),
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date(),
  startingCollateralUsd: NonNegativeDecimalStringSchema,
  /** The candle resolution the series was marked on: finer is exact, coarser for battles built after retention. */
  seriesSourceResolution: z.enum(["1s", "15s", "1m", "5m"]),
  players: z.array(BattleRecordPlayerSchema),
  cuts: z.array(BattleRecordCutSchema),
});
export type ArenaBattleRecordDto = z.infer<typeof ArenaBattleRecordSchema>;

// Error codes of `GET /v1/cede-arena/battle-record` a client branches on: NOT_AVAILABLE and EXPIRED mean
// "show the standard result page"; NOT_FINALIZED means "not yet" (served only once the battle is finalized);
// RECORD_PENDING means "being built": the room stream's BATTLE_RECORD_DONE frame says when to read again.
// INCONSISTENT is a failure the API reports itself.
export const ARENA_BATTLE_RECORD_ERROR_CODE = {
  NOT_AVAILABLE: "TRADE_TO_EARN_ARENA_BATTLE_RECORD_NOT_AVAILABLE", // 404: a duel, or a battle too large to record
  EXPIRED: "TRADE_TO_EARN_ARENA_BATTLE_RECORD_EXPIRED", // 410: past the candle or paper retention it is built from
  NOT_FINALIZED: "TRADE_TO_EARN_ARENA_BATTLE_RECORD_NOT_FINALIZED", // 409: the battle is live or settling
  RECORD_PENDING: "TRADE_TO_EARN_ARENA_BATTLE_RECORD_PENDING", // 409: finalized, its record not built yet
  NEWER_VERSION: "TRADE_TO_EARN_ARENA_BATTLE_RECORD_NEWER_VERSION", // 503: stored by a newer deploy (mixed deploy, rollback)
  INCONSISTENT: "TRADE_TO_EARN_ARENA_BATTLE_RECORD_INCONSISTENT", // 500: cannot be built or trusted
} as const;

export const ArenaBattleRecordQuerySchema = z.object({
  campaign: z.string().min(1).max(128),
  epoch: z.coerce.number().int().nonnegative().max(2_147_483_647),
});
